import React, { useState, useEffect, useCallback } from 'react';
import QRCode from 'qrcode';
import { X, QrCode, Loader2, Copy, Check } from 'lucide-react';
import api from '../services/api';
import { loadUTM, trackConversion } from '../utils/utm';
import { useAuth } from '../contexts/AuthContext';
import Button from './ui/Button';

const PaymentModal = ({ isOpen, onClose, packageData, onSuccess }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [pixData, setPixData] = useState(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [copied, setCopied] = useState(false);

  const [customerData, setCustomerData] = useState({
    name: '',
    email: '',
    phone_number: '(11) 99999-9999',
    document: '',
  });

  useEffect(() => {
    if (user?.email) {
      setCustomerData((prev) => ({
        ...prev,
        email: user.email,
        name: user.nome || prev.name,
      }));
    }
  }, [user]);

  const generateQRCode = async (pixCode) => {
    try {
      const dataUrl = await QRCode.toDataURL(pixCode, {
        width: 256,
        margin: 2,
        color: { dark: '#000000', light: '#FFFFFF' },
      });
      setQrCodeDataUrl(dataUrl);
    } catch (error) {
      console.error('Erro ao gerar QR Code:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || pixData) return;

    const email = String(customerData.email || '').trim().toLowerCase();
    if (!email || !email.includes('@')) {
      setMessageType('error');
      setMessage('Informe um email válido — você usará ele no cadastro.');
      return;
    }
    if (!customerData.document || customerData.document.length < 11) {
      setMessageType('error');
      setMessage('Informe um CPF válido (11 dígitos).');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const utm = loadUTM();
      const cartItem = {
        product_hash: packageData.product_hash || `tokens_${packageData.id}`,
        product_type: packageData.type || 'tokens',
        title:
          packageData.title ||
          `${packageData.tokens} Tokens - Borracha de Roupas`,
        cover: null,
        price: Math.round(packageData.price * 100),
        quantity: 1,
        operation_type: 1,
        tangible: false,
      };

      const paymentData = {
        amount: Math.round(packageData.price * 100),
        payment_method: 'pix',
        customer: {
          name: customerData.name || user?.nome || 'Cliente PIX',
          email,
          phone_number: customerData.phone_number || '(11) 99999-9999',
          document: customerData.document || '',
        },
        cart: [cartItem],
        installments: 1,
        expire_in_days: 1,
        utm,
      };

      const response = await api.post('/payment/create', paymentData);

      if (response.data.success) {
        const tx = {
          ...response.data.transaction,
          customer_email: email,
        };
        setPixData(tx);
        setPaymentStatus('pending');

        if (response.data.qr_code) {
          await generateQRCode(response.data.qr_code);
          setMessageType('success');
          setMessage('PIX gerado! Escaneie o QR Code ou copie o código.');
        } else if (response.data.payment_url) {
          setMessageType('success');
          setMessage('PIX gerado! Acesse o link para pagar.');
        } else {
          setMessageType('success');
          setMessage('PIX gerado! Verifique os dados da transação.');
        }
      } else {
        setMessageType('error');
        setMessage(response.data.message);
      }
    } catch (error) {
      setMessageType('error');
      setMessage(error.response?.data?.message || 'Erro ao processar pagamento');
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = useCallback(
    async (transactionHash) => {
      try {
        const response = await api.get(`/payment/status/${transactionHash}`);
        if (response.data.success) {
          const status = response.data.transaction.status;
          setPaymentStatus(status);

          if (status === 'approved') {
            trackConversion(response.data.transaction);
            onSuccess({
              ...response.data.transaction,
              customer_email:
                pixData?.customer_email ||
                response.data.transaction?.customer?.email ||
                customerData.email,
            });
            setMessageType('success');
            setMessage(
              packageData?.type === 'vip'
                ? 'Pagamento aprovado! VIP ativado.'
                : 'Pagamento aprovado! Tokens creditados.'
            );
            setTimeout(() => {
              onClose();
              setMessage('');
            }, 1500);
          } else if (status === 'cancelled' || status === 'failed') {
            setMessageType('error');
            setMessage('Pagamento cancelado ou falhou. Tente novamente.');
          }
        }
      } catch (error) {
        console.error('Erro ao verificar status:', error);
      }
    },
    [onSuccess, onClose, packageData, pixData, customerData.email]
  );

  useEffect(() => {
    let interval;
    if (pixData?.hash && paymentStatus === 'pending') {
      interval = setInterval(() => checkPaymentStatus(pixData.hash), 3000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [pixData, paymentStatus, checkPaymentStatus]);

  useEffect(() => {
    if (!isOpen) {
      setPixData(null);
      setQrCodeDataUrl('');
      setPaymentStatus('pending');
      setMessage('');
      setCopied(false);
    }
  }, [isOpen]);

  const copyPixCode = async () => {
    const code = pixData?.qr_code || pixData?.payment_url;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  if (!isOpen) return null;

  const inputClass =
    'w-full rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2.5 text-white placeholder-zinc-500 focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
      <div className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-white/10 bg-zinc-900 shadow-2xl">
        <div className="p-6">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white">Finalizar compra</h2>
            <button
              onClick={onClose}
              className="text-zinc-500 transition-colors hover:text-zinc-300"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="mb-6 flex items-center justify-between rounded-xl border border-white/10 bg-white/5 p-4">
            <span className="text-sm text-zinc-300">
              {packageData.type === 'vip'
                ? packageData.title || 'Upgrade VIP'
                : `${packageData.tokens} tokens`}
            </span>
            <span className="text-lg font-semibold text-white">
              R$ {packageData.price.toFixed(2)}
            </span>
          </div>

          {message && (
            <div
              className={`mb-4 rounded-lg px-4 py-2.5 text-center text-sm ${
                messageType === 'success'
                  ? 'border border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
                  : 'border border-red-500/30 bg-red-500/10 text-red-300'
              }`}
            >
              {message}
            </div>
          )}

          {pixData && qrCodeDataUrl && (
            <div className="mb-6 rounded-xl border border-white/10 bg-white/5 p-5 text-center">
              <h3 className="mb-4 text-sm font-medium text-white">
                Escaneie o QR Code com seu app de pagamento
              </h3>
              <div className="mb-4 flex justify-center">
                <img
                  src={qrCodeDataUrl}
                  alt="QR Code PIX"
                  className="rounded-lg border border-white/10"
                />
              </div>
              <p className="mb-2 text-xs text-zinc-400">Ou copie o código PIX</p>
              <div className="mb-3 break-all rounded-lg border border-white/10 bg-zinc-950 p-3 text-left text-xs text-zinc-300">
                {pixData.qr_code || pixData.payment_url}
              </div>
              <Button
                onClick={copyPixCode}
                variant="secondary"
                size="sm"
                icon={copied ? Check : Copy}
                className="mb-4"
              >
                {copied ? 'Copiado!' : 'Copiar código'}
              </Button>
              <div className="flex items-center justify-center gap-2 text-sm text-zinc-400">
                <Loader2 className="h-4 w-4 animate-spin text-rose-400" />
                Aguardando pagamento...
              </div>
            </div>
          )}

          <div className="mb-6">
            <h3 className="mb-3 text-sm font-medium text-zinc-300">Método de pagamento</h3>
            <div className="flex items-center gap-3 rounded-xl border border-rose-500/40 bg-rose-500/10 p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-rose-500/20">
                <QrCode className="h-5 w-5 text-rose-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">PIX</p>
                <p className="text-xs text-zinc-400">Aprovação instantânea</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <h3 className="mb-3 text-sm font-medium text-zinc-300">Dados pessoais</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-zinc-400">Nome</label>
                  <input
                    type="text"
                    value={customerData.name}
                    onChange={(e) =>
                      setCustomerData((prev) => ({ ...prev, name: e.target.value }))
                    }
                    placeholder="Seu nome"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-zinc-400">Email *</label>
                  <input
                    type="email"
                    value={customerData.email}
                    onChange={(e) =>
                      setCustomerData((prev) => ({ ...prev, email: e.target.value }))
                    }
                    placeholder="seu@email.com"
                    required
                    className={inputClass}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-xs font-medium text-zinc-400">
                    CPF (11 dígitos) *
                  </label>
                  <input
                    type="text"
                    value={customerData.document}
                    onChange={(e) =>
                      setCustomerData((prev) => ({
                        ...prev,
                        document: e.target.value.replace(/\D/g, '').slice(0, 11),
                      }))
                    }
                    placeholder="00000000000"
                    required
                    className={inputClass}
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button type="button" onClick={onClose} variant="secondary" fullWidth>
                Cancelar
              </Button>
              <Button type="submit" fullWidth loading={loading} disabled={!!pixData}>
                {loading ? 'Processando...' : `Pagar R$ ${packageData.price.toFixed(2)}`}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
